import {DefaultInfoProps} from "@models/DefaultProps.ts";
import {IContratoParcela} from "@models/contratos/parcelamento.ts";
import React, {ComponentProps, useMemo} from "react";
import {ToDateDefault, ToMoney} from "@shared/MathHelp.ts";
import {IconButton, TableCell, TableRow, Typography} from '@mui/material';
import {UsuarioTypo} from "@components/usuario/UsuarioTypo.tsx";
import {IContrato} from "@models/contratos/contrato.ts";
import {ContratoInfoNav} from "@components/contratos/ContratoInfoNav.tsx";
import {useContrato} from "@components/contratos/hooks/useContrato.tsx";
import {ContratoParcelaNav} from "@components/contratos/parcelas/ContratoParcelaNav.tsx";
import {MoneyInputEntry} from "@shared/specialComponents/InputEntry.tsx";
import dayjs from "dayjs";
import {DatePicker} from "@mui/x-date-pickers";
import {DeleteOutlined, RestoreFromTrashOutlined} from "@mui/icons-material";

interface ContratoParcelaRowProps extends DefaultInfoProps<IContratoParcela | number> {
    slotProps?: {
        contratoNav?: (c: IContrato | number) => ComponentProps<typeof ContratoInfoNav>
        contratoParcelaNav?: (c: IContratoParcela) => ComponentProps<typeof ContratoParcelaNav>
    },
    componentOverrides?: {
        ContratoCell?: (data: { contrato: IContrato | number, parcela: IContratoParcela }) => React.ReactNode
        ParcelaCell?: (data: { parcela: IContratoParcela | number }) => React.ReactNode
    },
    editing?: boolean;
    disabled?: boolean;
    removing?: boolean;
    onChange?: (parcela: IContratoParcela) => void;
    onRemove?: (parcela: IContratoParcela) => void;
}

export function ContratoParcelaRow(props: ContratoParcelaRowProps) {
    const {componentOverrides, slotProps} = props
    const {ContratoCell, ParcelaCell} = componentOverrides ?? {}
    const {contratoNav, contratoParcelaNav} = slotProps ?? {}
    const {useParcelaValue} = useContrato()

    const [value, setValue, originalValue] = useParcelaValue(props.value)

    const contratoNavProps = () => {
        if (contratoNav && value != null)
            return contratoNav(value.contrato ?? value.contratoId)
        else
            return {}
    }

    const contratoParcelaNavProps = () => {
        if (contratoParcelaNav && value != null)
            return contratoParcelaNav(value)
        else
            return {}
    }

    const valorChanged = useMemo(() => {
        return originalValue?.valor != value?.valor && value?.valor != null;
    }, [originalValue?.valor, value?.valor,])

    const vencto = useMemo(() => {
        return dayjs(value?.dataVencimento)
    }, [value, originalValue?.dataVencimento])

    const venctoChanged = useMemo(() => {
        return !dayjs(originalValue?.dataVencimento).isSame(vencto)
    }, [originalValue?.dataVencimento, vencto])

    const canChange = useMemo(() => {
        return !(value != null && value.pago > 0 || props.disabled == true || value?.status != 'Pendente' || props.removing === true);
    }, [value, props]);

    const isAtrasado = useMemo(() => {
        if (value?.status == null) return false
        if (['Pendente', 'Aprovado'].includes(value.status)) {
            if (value.pago > 0) return false
            return dayjs(value.dataVencimento).isBefore(dayjs())
        }
        return false
    }, [value?.dataVencimento, value?.pago, value?.status])

    const statusColor = useMemo(() => {
        if (value?.status == null) return undefined
        return {
            'Pendente': 'warning',
            'Aprovado': 'success',
            'Cancelado': 'error',
            'Finalizado': 'info',
            'Rejeitado': 'error'
        }[value.status]
    }, [value?.status])


    return (value && <TableRow sx={{opacity: props.removing ? 0.6 : 1}}>
            <TableCell>{ParcelaCell && <ParcelaCell parcela={value}/> ||
                <ContratoParcelaNav  {...contratoParcelaNavProps()} value={value} tableMode/>}</TableCell>
            <TableCell>{ContratoCell && <ContratoCell contrato={value.contrato ?? value.contratoId}
                                                      parcela={value}/> ||
                <ContratoInfoNav {...contratoNavProps()} tableMode value={value.contratoId}/>}</TableCell>
            <TableCell><UsuarioTypo typographyProps={{variant: 'caption'}}
                                    usuarioId={value.usuarioCriacaoId}/></TableCell>
            <TableCell sx={{color: 'grey'}}>{ToDateDefault(value.dataCriacao)}</TableCell>
            <TableCell><Typography color={statusColor}>{value.status}</Typography></TableCell>
            <TableCell>{props.editing && <MoneyInputEntry value={value.valor}
                                                          disabled={!canChange}
                                                          label={'valor'}
                                                          textFieldProps={{
                                                              color: valorChanged ? 'primary' : undefined,
                                                              focused: valorChanged
                                                          }}
                                                          onChange={(e) => {
                                                              setValue(prev => {
                                                                  if (prev != null) {
                                                                      if (props.onChange)
                                                                          props.onChange({
                                                                              ...prev,
                                                                              valor: e ?? value?.valor
                                                                          })

                                                                      return ({
                                                                          ...prev,
                                                                          valor: e ?? value?.valor
                                                                      })
                                                                  }
                                                              })
                                                          }}/> || ToMoney(value.valor)}
            </TableCell>
            <TableCell>{ToMoney(value.pago)}</TableCell>
            <TableCell>{props.editing &&
                <DatePicker value={vencto}
                            disabled={!canChange}
                            label={'Vencimento'}
                            onChange={(e) => {
                                setValue(prev => {
                                    if (prev != null) {
                                        if (props.onChange)
                                            props.onChange({
                                                ...prev,
                                                dataVencimento: e ?? value?.dataVencimento
                                            })

                                        return ({
                                            ...prev,
                                            dataVencimento: e ?? value?.dataVencimento
                                        })
                                    }
                                })
                            }}
                            slotProps={{
                                textField: {
                                    color: venctoChanged ? 'primary' : undefined,
                                    focused: venctoChanged,
                                    error: isAtrasado
                                }
                            }}
                /> || ToDateDefault(value.dataVencimento)}</TableCell>
            <TableCell>{value.observacao}</TableCell>
            <TableCell><IconButton disabled={props.onRemove == null || (!props.editing) || (!canChange && !props.removing)}
                                   color={props.removing ? 'warning' : 'info'}
                                   onClick={() => {
                                       if (props.onRemove)
                                           props.onRemove(value)
                                   }}>
                {props.removing ? <RestoreFromTrashOutlined/> : <DeleteOutlined/>}
            </IconButton></TableCell>
        </TableRow>
    )
        ;
}